import React, { useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Settings,
  ChevronDown,
  Users,
  TrendingUp,
  Wrench,
  MessageCircle,
  FileText,
} from "lucide-react";
import { type LucideIcon } from "lucide-react";
import logoLyraxDark from '@/assets/images/logo-lyrax.png'
import logoLyraxWhite from '@/assets/images/logo-lyrax-white.png'
import logoLyrax from '@/styles/assets/logo-lyrax.png'
import { useAuth } from "@/hooks/useAuth";

export interface MenuItem {
  label: string;
  icon?: LucideIcon;
  path?: string;
  children?: MenuItem[];
  requiredPermissions?: string[];
  /** Tenant feature key / channel slug required (e.g. virtualpos) */
  requiredFeature?: string;
}

const menuItems: MenuItem[] = [
  {
    label: "Dashboards",
    icon: LayoutDashboard,
    children: [
      { label: "General", path: "/dashboard/general", requiredPermissions: ["dashboard:general"] },
      { label: "Suscriptores", path: "/dashboard/suscriptores", requiredPermissions: ["dashboard:suscriptores"] },
      { label: "Financiero", path: "/dashboard/financiero", requiredPermissions: ["dashboard:financiero"] },
    ],
  },
  {
    label: "Gestión de Suscriptores",
    icon: Users,
    children: [
      { label: "Cartera", path: "/subscribers/cartera", requiredPermissions: ["dashboard:suscriptores"] },
      { label: "VirtualPos", path: "/subscribers/virtual-pos", requiredPermissions: ["virtualpos:read"], requiredFeature: "virtualpos" },
      { label: "Payku", path: "/subscribers/payku", requiredPermissions: ["payku:read"], requiredFeature: "payku" },
      { label: "Mandatos", path: "/subscribers/mandatos", requiredPermissions: ["mandatos:read"], requiredFeature: "mandatos" },
      { label: "Donando", path: "/subscribers/donando", requiredFeature: "donando" },
      // { label: "Socios Análogos", path: "/subscribers/socios-analogos" },
    ],
  },
  {
    label: "Control de Gestión",
    icon: TrendingUp,
    children: [
      { label: "Forecast", path: "/management/forecast" },
      { label: "Presupuesto", path: "/management/presupuesto" },
      { label: "Proyecciones", path: "/management/proyecciones" },
    ],
  },
  {
    label: "Mantenedores",
    icon: Wrench,
    children: [
      { label: "Usuarios", path: "/mantenants/usuarios" },
      { label: "Estados", path: "/mantenants/estados" },
      { label: "Bancos", path: "/mantenants/bancos" },
    ],
  },
  {
    label: "Reportes",
    icon: FileText,
    path: "/reports",
  },
  {
    label: "Configuración",
    icon: Settings,
    children: [
      { label: "Usuarios", path: "/settings/usuarios", requiredPermissions: ["settings:usuarios", "users:read"] },
      { label: "Integraciones", path: "/settings/integrations", requiredPermissions: ["settings:integrations"] },
      { label: "Logs de Integración", path: "/settings/integration-logs", requiredPermissions: ["settings:integration-logs"] },
    ],
  },
  {
    label: "Asistente IA",
    icon: MessageCircle,
    path: "/chatbot",
  },
];

function Icon({ icon: IconComp, className }: { icon?: LucideIcon; className?: string }) {
  return IconComp ? <IconComp className={className} /> : null;
}

type SidebarProps = { collapsed: boolean };

export const Sidebar: React.FC<SidebarProps> = ({ collapsed }) => {
  // Todos los menús abiertos por defecto
  const [openMenus, setOpenMenus] = useState<string[]>([
    "Dashboards",
    "Gestión de Suscriptores",
    "Control de Gestión",
    "Mantenedores"
  ]);
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const menuItemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { user } = useAuth();

  const calculatePosition = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    return {
      top: rect.top,
      left: rect.right + 8
    };
  };

  const handleMouseEnter = (itemLabel: string) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    const ref = menuItemRefs.current.get(itemLabel);
    if (ref) {
      setMenuPosition(calculatePosition(ref));
    }
    setHoveredMenu(itemLabel);
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setHoveredMenu(null);
      setMenuPosition(null);
    }, 100);
  };

  const permissionSet = useMemo(() => {
    const permissions = user?.role?.permissions ?? [];
    return new Set(permissions.map((permission) => permission.name));
  }, [user]);

  const featureSet = useMemo(() => {
    const features = user?.features ?? [];
    const channels = user?.channels ?? [];
    return new Set([...features, ...channels]);
  }, [user]);

  const hasPermission = (required?: string[]) => {
    if (!required || required.length === 0) return true;
    return required.some((permission) => permissionSet.has(permission));
  };

  const hasFeature = (required?: string) => {
    if (!required) return true;
    // If profile has not loaded features yet, do not hide (avoid flash-empty menus)
    if (!user?.features && !user?.channels) return true;
    return featureSet.has(required);
  };

  const filteredMenuItems = useMemo(() => {
    return menuItems
      .map((item) => {
        if (item.children?.length) {
          const filteredChildren = item.children.filter(
            (child) =>
              hasPermission(child.requiredPermissions) && hasFeature(child.requiredFeature),
          );
          if (filteredChildren.length === 0) {
            return hasPermission(item.requiredPermissions) &&
              hasFeature(item.requiredFeature) &&
              item.path
              ? { ...item, children: [] }
              : null;
          }
          return { ...item, children: filteredChildren };
        }
        return hasPermission(item.requiredPermissions) && hasFeature(item.requiredFeature)
          ? item
          : null;
      })
      .filter((item): item is MenuItem => item !== null);
  }, [permissionSet, featureSet, user]);

  const toggleMenu = (label: string) => {
    setOpenMenus((prev) =>
      prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]
    );
  };

  return (
    <aside
      className={`${collapsed ? "w-20" : "w-64"
        } bg-lyrax-surface flex flex-col transition-all duration-300`}
    >
      {/* Logo */}
      <div className="h-20 flex items-center justify-center">
        {collapsed ? (
          <div className="flex items-center justify-center h-10 w-10 bg-lyrax-bg rounded-xl shadow-inner border border-lyrax-primary/20 transition-all duration-300">
            <span className="text-2xl font-bold text-lyrax-primary tracking-tighter">L</span>
          </div>
        ) : (
          <>
            <img
              src={logoLyraxDark}
              alt="Lyrax Logo"
              className="h-12 w-auto object-contain dark:hidden transition-all duration-300"
            />
            <img
              src={logoLyraxWhite}
              alt="Lyrax Logo"
              className="h-12 w-auto object-contain hidden dark:block transition-all duration-300"
            />
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-visible">
        {filteredMenuItems.map((item) => {
          const hasChildren = !!item.children?.length;
          const isOpen = openMenus.includes(item.label);

          if (collapsed) {
            if (hasChildren) {
              return (
                <div key={item.label} className="relative group"
                  ref={(el) => {
                    if (el) menuItemRefs.current.set(item.label, el);
                    else menuItemRefs.current.delete(item.label);
                  }}
                  onMouseEnter={() => handleMouseEnter(item.label)}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    className="flex items-center justify-center w-full p-2.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    title={item.label}
                  >
                    <Icon icon={item.icon} className="w-5 h-5" />
                  </button>
                </div>
              );
            }

            return (
              <NavLink
                key={item.label}
                to={item.path || "#"}
                className={({ isActive }) =>
                  `flex items-center justify-center p-2.5 rounded-lg transition-colors ${isActive
                    ? "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`
                }
                title={item.label}
              >
                <Icon icon={item.icon} className="w-5 h-5" />
              </NavLink>
            );
          }

          // Sidebar expandido
          return (
            <div key={item.label}>
              {hasChildren ? (
                <button
                  onClick={() => toggleMenu(item.label)}
                  className="flex items-center gap-3 w-full px-3 py-2 rounded hover:bg-lyrax-surface-hover hover:text-lyrax-primary transition-colors duration-200 text-lyrax-text-secondary"
                >
                  <Icon icon={item.icon} className="w-5 h-5 flex-shrink-0" />
                  <span className="flex-1 text-left text-sm font-medium">{item.label}</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""
                      }`}
                  />
                </button>
              ) : (
                <NavLink
                  to={item.path || "#"}
                  className={({ isActive }) =>
                    `flex items-center gap-3 w-full px-3 py-2 rounded hover:bg-lyrax-surface-hover hover:text-lyrax-primary transition-colors duration-200 ${isActive ? "bg-lyrax-bg text-lyrax-primary font-bold shadow-inner" : "text-lyrax-text-secondary"
                    }`
                  }
                >
                  <Icon icon={item.icon} className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-medium">{item.label}</span>
                </NavLink>
              )}

              {/* Submenús */}
              {hasChildren && isOpen && !collapsed && (
                <div className="ml-9 mt-1 space-y-0.5">
                  {item.children!.map((child) => (
                    <NavLink
                      key={child.label}
                      to={child.path || "#"}
                      className={({ isActive }) => {
                        const activeClass = isActive ? 'text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-900/20' : 'text-gray-600 dark:text-gray-400';
                        return `flex items-center px-3 py-2 rounded-lg text-sm transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 ${activeClass}`;
                      }}
                    >
                      {child.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer - Powered by Lyrax */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        {collapsed ? (
          <div className="flex items-center justify-center">
            <img
              src={logoLyrax}
              alt="Lyrax"
              className="h-6 object-contain opacity-60"
            />
          </div>
        ) : (
          <div className="flex items-center justify-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <span>Powered by</span>
            <img
              src={logoLyrax}
              alt="Lyrax"
              className="h-5 object-contain"
            />
          </div>
        )}
      </div>

      {/* Portal para menús flotantes */}
      {hoveredMenu && menuPosition && (
        createPortal(
          <div
            className="fixed bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 min-w-[180px] py-2 z-[9999] cursor-default"
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => {
              if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
              }
              setHoveredMenu(hoveredMenu);
            }}
            onMouseLeave={() => {
              hideTimeoutRef.current = setTimeout(() => {
                setHoveredMenu(null);
                setMenuPosition(null);
              }, 100);
            }}
          >
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
              {hoveredMenu}
            </div>
            {filteredMenuItems.find(item => item.label === hoveredMenu)?.children?.map((child) => (
              <NavLink
                key={child.label}
                to={child.path || "#"}
                className={({ isActive }) =>
                  `block px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer ${isActive ? "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 font-medium" : ""}`
                }
              >
                {child.label}
              </NavLink>
            ))}
          </div>,
          document.body
        )
      )}
    </aside>
  );
};
